/* @jsx h */
// @ts-nocheck
import { defineComponent, h, ref, computed, watch, onMounted } from "vue";
import {
  FastQuantitySelector,
  RightSheet,
  useLocalization,
} from "@lemmonhq/uikit";
import {
  RestaurantCustomer,
  ReservationTag,
  Reservation,
  TableStatus,
  FloorPlan,
} from "@lemmonhq/types";
import { api } from "@app/data/api";
import { CustomerSearchPanel } from "./CustomerSearchPanel";
import { CustomerCreatePanel } from "./CustomerCreatePanel";
import { CreateReservationData } from "@app/server/routes/shop/routes";
import { useAuth } from "@app/vue/providers/Auth";
import {
  getRestaurantDateKey,
  getRestaurantDateTimeUtc,
} from "@lemmonhq/blogic";
import { TableSelectionPanel } from "../../../ReservationTabs/TableSelectionPanel";
import { type TableSelectorTable } from "@app/vue/components/Business/TableSelector";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { ReservationDurationField } from "../ReservationDurationField";
import { listReservations } from "../../../../utils/trpcReservations";

export interface CreateReservationFormProps {
  onSubmit: (formData: CreateReservationData) => Promise<void>;
  onCancel: () => void;
  initialDate?: string;
  initialTime?: string;
  initialTableId?: number;
  initialDuration?: number;
}

type CustomerMode = "search" | "create";

export const CreateReservationForm =
  defineComponent<CreateReservationFormProps>({
    name: "CreateReservationForm",
    props: [
      "onSubmit",
      "onCancel",
      "initialDate",
      "initialTime",
      "initialTableId",
      "initialDuration",
    ],
    setup(props) {
      const { user } = useAuth();
      const { t } = useLocalization();
      const trpc = useTrpc();
      const restaurantTimeZone = computed(
        () => user.value?.restaurant?.timezone || "UTC",
      );

      // ── Customer state ──
      const selectedCustomer = ref<RestaurantCustomer | null>(null);
      const customerSearch = ref("");
      const searchResults = ref<RestaurantCustomer[]>([]);
      const isSearching = ref(false);
      const searchError = ref("");
      const customerMode = ref<CustomerMode>("search");
      const newCustomerForm = ref({ name: "", email: "", phone: "" });
      const newCustomerError = ref("");
      const isCreatingCustomer = ref(false);

      // ── Date & Time state ──
      const getTodayDate = () => {
        return getRestaurantDateKey(new Date(), restaurantTimeZone.value);
      };

      const selectedDate = ref(props.initialDate || getTodayDate());
      const selectedTime = ref(props.initialTime || "");
      const partySize = ref(2);
      // Reactive: re-evaluates when user.value?.restaurant_settings loads async.
      const defaultPeriodMinutes = computed(() => {
        const setting = user.value?.restaurant_settings?.find(
          (s) => s.key === "reservations_time_slot_minutes",
        );
        const parsed = setting ? parseInt(setting.value as string, 10) : NaN;
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 60;
      });
      // Initialise from props or from the setting (whichever is available first).
      // If settings haven't loaded yet, watch fires once when they arrive so the
      // duration field shows the correct default without requiring a re-mount.
      const durationMins = ref(
        props.initialDuration ?? defaultPeriodMinutes.value,
      );
      if (!props.initialDuration) {
        const stopWatch = watch(defaultPeriodMinutes, (val) => {
          durationMins.value = val;
          stopWatch();
        });
      }

      // ── Table state ──
      const selectedTableIds = ref<number[]>(
        props.initialTableId ? [props.initialTableId] : [],
      );
      const loadingTables = ref(false);
      const allTables = ref<TableStatus[]>([]);
      const dayReservations = ref<Reservation[]>([]);
      const tablesError = ref("");
      const floorPlans = ref<FloorPlan[]>([]);

      // ── Notes & Tags state ──
      const notes = ref("");
      const selectedTagIds = ref<string[]>([]);
      const availableTags = ref<ReservationTag[]>([]);
      const loadingTags = ref(false);

      // ── Form state ──
      const loading = ref(false);
      const error = ref("");

      // ── Computed ──
      const canSubmit = computed(
        () =>
          !!selectedCustomer.value &&
          !!selectedDate.value &&
          !!selectedTime.value &&
          selectedTime.value.trim() !== "" &&
          partySize.value > 0 &&
          durationMins.value > 0,
      );

      const dateTimeReady = computed(
        () =>
          !!selectedDate.value &&
          !!selectedTime.value &&
          selectedTime.value.trim() !== "" &&
          durationMins.value > 0,
      );

      // ── Table availability (same pattern as ReservationDetails) ──
      const tablesWithAvailability = computed(() => {
        if (allTables.value.length === 0) return [];

        // If no time selected yet, show all tables as free
        if (!dateTimeReady.value) {
          return allTables.value.map((table) => ({
            table,
            reservations: [] as Reservation[],
            isAvailable: true,
            hasConflict: false,
          }));
        }

        const requestedStart = new Date(
          getRestaurantDateTimeUtc(
            selectedDate.value,
            selectedTime.value || "12:00",
            restaurantTimeZone.value,
          ),
        );
        const requestedEnd = new Date(
          requestedStart.getTime() + durationMins.value * 60000,
        );

        const result = allTables.value.map((table) => {
          const tableReservations = dayReservations.value.filter(
            (reservation) =>
              reservation.table_assignments?.some(
                (assignment) => assignment.table_id === table.id,
              ),
          );
          const hasConflict = tableReservations.some((reservation) => {
            const resStart = new Date(reservation.time);
            const resEnd = new Date(
              resStart.getTime() + reservation.duration_mins * 60000,
            );
            return requestedStart < resEnd && requestedEnd > resStart;
          });
          return {
            table,
            reservations: tableReservations,
            isAvailable: !hasConflict,
            hasConflict,
          };
        });

        result.sort((a, b) => {
          if (a.isAvailable !== b.isAvailable) return a.isAvailable ? -1 : 1;
          return a.table.table.name.localeCompare(b.table.table.name);
        });

        return result;
      });

      const getRoom = (tableId: number) => {
        for (const fp of floorPlans.value) {
          if (fp.plan?.some((t) => t.tableId === tableId))
            return { name: fp.name, rank: fp.rank ?? 999 };
        }
        return { name: undefined, rank: 999 };
      };

      const tableSelectorData = computed<TableSelectorTable[]>(() =>
        tablesWithAvailability.value.map(
          ({ table, hasConflict, reservations }) => ({
            id: table.id,
            name: table.table.name,
            roomName: getRoom(table.id).name,
            roomRank: getRoom(table.id).rank,
            availability: hasConflict ? ("busy" as const) : ("free" as const),
            reservationsCount: reservations.length,
            disabled: false,
          }),
        ),
      );

      const availableTablesCount = computed(
        () => tablesWithAvailability.value.filter((t) => t.isAvailable).length,
      );

      // ── Load tables data ──
      const loadTablesData = async () => {
        if (!selectedDate.value) return;
        try {
          loadingTables.value = true;
          tablesError.value = "";
          const results = await Promise.all([
            api.getTablesMeta(),
            listReservations(trpc, {
              date: selectedDate.value,
              timezone: restaurantTimeZone.value,
              limit: "200",
            }),
            api.getFloorPlans(),
          ]);
          allTables.value = results[0] || [];
          dayReservations.value =
            (results[1] as { data: Reservation[] }).data || [];
          floorPlans.value = results[2] || [];
        } catch (e) {
          console.error("Failed to load tables data:", e);
          tablesError.value = "Failed to load tables data";
        } finally {
          loadingTables.value = false;
        }
      };

      // Reload tables when date changes
      watch(selectedDate, () => {
        loadTablesData();
      });

      // Initial load
      loadTablesData();

      // ── Load tags ──
      const loadTags = async () => {
        try {
          loadingTags.value = true;
          const result = await trpc.reservationTags.list.query();
          availableTags.value = result;
        } catch (e) {
          console.error("Failed to load reservation tags:", e);
        } finally {
          loadingTags.value = false;
        }
      };

      onMounted(() => {
        loadTags();
      });

      // ── Customer search with debounce ──
      let searchTimeout: any = null;
      watch(customerSearch, (value) => {
        if (searchTimeout) clearTimeout(searchTimeout);

        if (value.trim().length < 2) {
          searchResults.value = [];
          return;
        }

        searchTimeout = setTimeout(async () => {
          try {
            isSearching.value = true;
            searchError.value = "";
            const result = await api.getCustomers({ search: value, limit: 10 });
            searchResults.value = result.data || [];
          } catch (e) {
            searchError.value = "Failed to search customers";
            console.error("Customer search error:", e);
          } finally {
            isSearching.value = false;
          }
        }, 300);
      });

      // ── Handlers ──
      const handleSelectCustomer = (customer: RestaurantCustomer) => {
        selectedCustomer.value = customer;
        customerSearch.value = "";
        searchResults.value = [];
      };

      const handleClearSelectedCustomer = () => {
        selectedCustomer.value = null;
      };

      const handleModeChange = (value?: CustomerMode) => {
        customerMode.value = value ?? "search";
      };

      const handleUpdateNewCustomerField = (
        field: "name" | "email" | "phone",
        value: string,
      ) => {
        newCustomerForm.value[field] = value;
      };

      const handleCreateCustomer = async () => {
        try {
          isCreatingCustomer.value = true;
          newCustomerError.value = "";

          if (!newCustomerForm.value.name) {
            newCustomerError.value = t("reservations.create.name_required");
            return;
          }

          if (!newCustomerForm.value.email && !newCustomerForm.value.phone) {
            newCustomerError.value = t(
              "reservations.create.email_phone_required",
            );
            return;
          }

          const newCustomer = await api.createCustomer({
            name: newCustomerForm.value.name,
            email: newCustomerForm.value.email || undefined,
            phone: newCustomerForm.value.phone || undefined,
          });

          selectedCustomer.value = newCustomer;
          customerMode.value = "search";
          newCustomerForm.value = { name: "", email: "", phone: "" };
        } catch (e) {
          newCustomerError.value =
            e instanceof Error
              ? e.message
              : t("reservations.create.failed_customer");
        } finally {
          isCreatingCustomer.value = false;
        }
      };

      const handleTableToggle = (newIds: number[]) => {
        selectedTableIds.value = newIds;
      };

      const toggleTag = (tagId: string) => {
        const current = [...selectedTagIds.value];
        const idx = current.indexOf(tagId);
        if (idx >= 0) {
          current.splice(idx, 1);
        } else {
          current.push(tagId);
        }
        selectedTagIds.value = current;
      };

      const handleSubmit = async () => {
        if (!canSubmit.value) return;

        try {
          loading.value = true;
          error.value = "";

          const dateTimeUtc = getRestaurantDateTimeUtc(
            selectedDate.value,
            selectedTime.value,
            restaurantTimeZone.value,
          );
          const c = selectedCustomer.value;
          if (!c) throw new Error("customer not found");

          const partyName =
            c.name ||
            (c.email ? c.email.split("@")[0] : null) ||
            c.phone ||
            "Guest";

          await props.onSubmit({
            customer_id: c.customer_id,
            time: dateTimeUtc,
            party_size: partySize.value,
            duration_mins: durationMins.value,
            notes: notes.value || undefined,
            table_ids:
              selectedTableIds.value.length > 0
                ? selectedTableIds.value
                : undefined,
            tag_ids:
              selectedTagIds.value.length > 0
                ? selectedTagIds.value
                : undefined,
            party_email: c.email || undefined,
            party_phone: c.phone || undefined,
            party_name: partyName,
          });
        } catch (e) {
          error.value =
            e instanceof Error
              ? e.message
              : t("reservations.create.failed_reservation");
        } finally {
          loading.value = false;
        }
      };

      return () => (
        <RightSheet
          isOpen={true}
          onClose={props.onCancel}
          title={t("reservations.create.create_new")}
          width="480px"
          mobileVariant="full-screen"
        >
          <div class="flex flex-col h-full">
            {/* Scrollable content */}
            <div class="flex-1 overflow-y-auto">
              <div class="px-5 py-5 space-y-5">
                {/* ── GUEST section ── */}
                <div>
                  <div class="flex items-center justify-between mb-3">
                    <h4 class="text-xs font-semibold text-accent uppercase tracking-wider">
                      {t("reservations.form.customer_title")}
                    </h4>
                    {!selectedCustomer.value && (
                      <div
                        class="relative flex items-stretch rounded-lg overflow-hidden border border-base-content/10"
                        style="height: 32px; min-height: 32px; background: rgba(0,0,0,0.3); box-shadow: inset 0 2px 6px rgba(0,0,0,0.5), inset 0 1px 3px rgba(0,0,0,0.3);"
                      >
                        <div
                          class="absolute top-0 bottom-0 transition-transform duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
                          style={{
                            width: "50%",
                            transform:
                              customerMode.value === "search"
                                ? "translateX(0)"
                                : "translateX(100%)",
                          }}
                        >
                          <div
                            class="h-full rounded-lg"
                            style="background: linear-gradient(180deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 100%); box-shadow: 0 1px 4px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.12); border: 1px solid rgba(255,255,255,0.08);"
                          />
                        </div>
                        <button
                          class={`relative z-10 flex items-center justify-center gap-1 text-xs font-semibold transition-colors duration-300 cursor-pointer ${
                            customerMode.value === "search"
                              ? "text-accent"
                              : "text-base-content/30 hover:text-base-content/50"
                          }`}
                          style="min-width: 60px; padding: 0 12px;"
                          onClick={() => handleModeChange("search")}
                        >
                          <span class="material-symbols-outlined text-sm">
                            search
                          </span>
                          Find
                        </button>
                        <button
                          class={`relative z-10 flex items-center justify-center gap-1 text-xs font-semibold transition-colors duration-300 cursor-pointer ${
                            customerMode.value === "create"
                              ? "text-accent"
                              : "text-base-content/30 hover:text-base-content/50"
                          }`}
                          style="min-width: 60px; padding: 0 12px;"
                          onClick={() => handleModeChange("create")}
                        >
                          <span class="material-symbols-outlined text-sm">
                            person_add
                          </span>
                          New
                        </button>
                      </div>
                    )}
                  </div>

                  {selectedCustomer.value ? (
                    (() => {
                      const customer = selectedCustomer.value!;
                      const displayName =
                        customer.name ||
                        customer.email ||
                        customer.phone ||
                        t("reservations.form.unnamed_customer");
                      const contactInfo = [customer.email, customer.phone]
                        .filter(Boolean)
                        .join(" \u2022 ");

                      return (
                        <div class="rounded-xl border border-success/25 bg-success/5 px-4 py-3 flex items-center justify-between">
                          <div class="flex items-center gap-3 min-w-0">
                            <div class="avatar placeholder">
                              <div class="bg-success/10 text-success rounded-full w-9 h-9 flex items-center justify-center font-semibold text-sm">
                                {displayName.charAt(0).toUpperCase()}
                              </div>
                            </div>
                            <div class="min-w-0">
                              <p class="font-semibold text-[13px] text-success truncate">
                                {displayName}
                              </p>
                              {contactInfo && (
                                <p class="text-[11px] text-success/60 truncate">
                                  {contactInfo}
                                </p>
                              )}
                            </div>
                          </div>
                          <button
                            class="btn btn-ghost btn-xs"
                            onClick={handleClearSelectedCustomer}
                          >
                            {t("general.words.change")}
                          </button>
                        </div>
                      );
                    })()
                  ) : customerMode.value === "create" ? (
                    <CustomerCreatePanel
                      form={newCustomerForm.value}
                      onUpdateField={handleUpdateNewCustomerField}
                      error={newCustomerError.value}
                      isSubmitting={isCreatingCustomer.value}
                      onSubmit={handleCreateCustomer}
                      onCancel={() => handleModeChange("search")}
                    />
                  ) : (
                    <CustomerSearchPanel
                      customerSearch={customerSearch.value}
                      onCustomerSearch={(value) =>
                        (customerSearch.value = value)
                      }
                      isSearching={isSearching.value}
                      searchResults={searchResults.value}
                      searchError={searchError.value}
                      onSelectCustomer={handleSelectCustomer}
                    />
                  )}
                </div>

                {/* ── Divider ── */}
                <div class="border-t border-base-content/8" />

                {/* ── DATE & TIME section ── */}
                <div>
                  <h4 class="text-xs font-semibold text-accent uppercase tracking-wider mb-3">
                    {t("reservations.seat_sheet.date_time")}
                  </h4>

                  <div class="space-y-3">
                    {/* Row 1: Date + Time */}
                    <div class="grid grid-cols-2 gap-3">
                      <input
                        type="date"
                        class="input input-bordered w-full text-[13px]"
                        style="height: 36px; min-height: 36px;"
                        value={selectedDate.value}
                        onChange={(e: any) => {
                          const v = e.target.value;
                          // Only commit when a full YYYY-MM-DD date is selected
                          if (v && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
                            selectedDate.value = v;
                          }
                        }}
                      />
                      <input
                        type="time"
                        step="900"
                        class="input input-bordered w-full text-[13px]"
                        style="height: 36px; min-height: 36px;"
                        value={selectedTime.value}
                        onInput={(e: any) =>
                          (selectedTime.value = e.target.value)
                        }
                      />
                    </div>

                    <div class="space-y-2">
                      <div class="flex items-center gap-2">
                        <span class="material-symbols-outlined text-base text-base-content/55">
                          group
                        </span>
                        <span class="text-xs text-accent">Guests</span>
                      </div>
                      <FastQuantitySelector
                        value={partySize.value > 0 ? partySize.value : null}
                        onChange={(value) => {
                          partySize.value = value ?? 0;
                        }}
                      />
                    </div>

                    <div>
                      <span class="label-text text-xs font-semibold uppercase tracking-wider text-accent mb-2 block">
                        Duration
                      </span>
                      <div class="h-12 px-3 rounded-xl flex items-center">
                        <div class="flex items-center gap-2 flex-1">
                          <ReservationDurationField
                            value={durationMins.value}
                            onChange={(value) => {
                              durationMins.value = value;
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Divider ── */}
                <div class="border-t border-base-content/8" />

                {/* ── TABLE section ── */}
                <div>
                  <TableSelectionPanel
                    tables={tableSelectorData.value}
                    selectedIds={selectedTableIds.value}
                    onChange={handleTableToggle}
                    loading={loadingTables.value}
                    error={tablesError.value}
                    onRetry={loadTablesData}
                    availableCount={availableTablesCount.value}
                    totalCount={allTables.value.length}
                    selectionDisabled={false}
                  />
                </div>

                {/* ── Divider ── */}
                <div class="border-t border-base-content/8" />

                {/* ── NOTES & TAGS section ── */}
                <div>
                  <h4 class="text-xs font-semibold text-base-content/45 uppercase tracking-wider mb-3">
                    Notes
                  </h4>

                  {/* Tags */}
                  {loadingTags.value && (
                    <div class="flex flex-wrap gap-2 mb-3.5">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          class="w-16 h-8 rounded-lg bg-base-300/30 animate-pulse"
                        />
                      ))}
                    </div>
                  )}
                  {!loadingTags.value && availableTags.value.length > 0 && (
                    <div class="mb-3.5">
                      <div class="flex flex-wrap gap-2">
                        {availableTags.value.map((tag) => {
                          const isSelected = selectedTagIds.value.includes(
                            tag.id,
                          );
                          return (
                            <button
                              key={tag.id}
                              type="button"
                              class={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-all border cursor-pointer ${
                                isSelected
                                  ? "border-current shadow-sm"
                                  : "border-transparent opacity-40 hover:opacity-70"
                              }`}
                              style={{
                                backgroundColor: tag.color
                                  ? `${tag.color}${isSelected ? "25" : "10"}`
                                  : isSelected
                                    ? "oklch(var(--p) / 0.12)"
                                    : "oklch(var(--b3))",
                                color: tag.color || undefined,
                              }}
                              onClick={() => toggleTag(tag.id)}
                            >
                              {tag.icon && (
                                <span class="material-symbols-outlined text-sm">
                                  {tag.icon}
                                </span>
                              )}
                              {tag.name}
                              {isSelected && (
                                <span class="material-symbols-outlined text-[10px]">
                                  check
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Notes textarea */}
                  <textarea
                    class="textarea textarea-bordered w-full h-20 text-[13px]"
                    placeholder="Add notes or special requests..."
                    value={notes.value}
                    onInput={(e: any) => (notes.value = e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* ── Footer ── */}
            <div class="flex-shrink-0 border-t border-base-content/10 bg-base-100 px-5 py-3.5">
              {error.value && (
                <div class="alert alert-error mb-3 py-2 text-sm">
                  <span class="material-symbols-outlined text-base">error</span>
                  <span>{error.value}</span>
                </div>
              )}
              <div class="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={props.onCancel}
                  class="btn btn-ghost btn-sm"
                  disabled={loading.value}
                >
                  {t("reservations.general.cancel")}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading.value || !canSubmit.value}
                  class="btn btn-primary btn-sm"
                >
                  {loading.value && (
                    <span class="loading loading-spinner loading-sm"></span>
                  )}
                  {loading.value
                    ? t("reservations.create.creating")
                    : t("reservations.create.create_reservation")}
                </button>
              </div>
            </div>
          </div>
        </RightSheet>
      );
    },
  });
